import React, { useState, useEffect, useRef } from 'react';
import { Search, LogOut, User, Plus, Users, Menu } from 'lucide-react';
import './TopBar.css';

const TopBar = ({ 
  activeChat, 
  agents, 
  groups,
  onSearch, 
  onLogout, 
  username, 
  onAddAgent,
  onMobileMenuToggle,
  globalSearchTerm = '',
  globalSearchResults = [],
  onSearchResultClick,
  onClearSearch
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Clear search when active chat changes
  useEffect(() => {
    setSearchTerm('');
    if (onClearSearch) {
      onClearSearch();
    }
  }, [activeChat?.id, activeChat?.type, onClearSearch]);

  useEffect(() => {
    if (!showUserMenu) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowUserMenu(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUserMenu]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleClearSearchInput = () => {
    setSearchTerm('');
    if (onClearSearch) {
      onClearSearch();
    }
  };

  const getActiveChatInfo = () => {
    if (!activeChat) return { title: 'Select a chat', subtitle: '', memberCount: null };

    if (activeChat.type === 'agent') {
      const agent = agents.find(a => a.id === activeChat.id);
      return {
        title: agent?.name || 'Agent',
        subtitle: agent?.role || '',
        memberCount: null
      };
    } else if (activeChat.type === 'subChat') {
      const agent = agents.find(a => a.id === activeChat.parentId);
      const subChat = agent?.subChats?.find(s => s.id === activeChat.id);
      return {
        title: subChat?.name || 'Sub-chat',
        subtitle: `${agent?.name} workspace`,
        memberCount: null
      };
    } else if (activeChat.type === 'group') {
      const group = groups?.find(g => g.id === activeChat.id);
      return {
        title: group?.name || 'Group',
        subtitle: group?.description || 'Group conversation',
        memberCount: group?.members?.length || 0
      };
    }

    return { title: '', subtitle: '', memberCount: null };
  };

  const chatInfo = getActiveChatInfo();

  return (
    <div className="topbar">
      {/* Mobile Hamburger Menu */}
      <button 
        className="mobile-menu-button"
        onClick={onMobileMenuToggle}
        aria-label="Toggle menu"
      >
        <Menu size={24} />
      </button>

      <div className="topbar-left">
        <div className="chat-info">
          <h2 className="chat-title">
            {chatInfo.title}
            {chatInfo.memberCount !== null && (
              <span className="group-badge">
                <Users size={12} />
                {chatInfo.memberCount}
              </span>
            )}
          </h2>
          {chatInfo.subtitle && (
            <p className="chat-subtitle text-muted">{chatInfo.subtitle}</p>
          )}
        </div>
      </div>

      <div className="topbar-right">
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search all chats..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="search-clear-button"
              onClick={handleClearSearchInput}
              title="Clear search"
            >
              ×
            </button>
          )}

          {/* Global Search Results Dropdown */}
          {globalSearchResults.length > 0 && (
            <>
              <div 
                className="search-dropdown-overlay"
                onClick={handleClearSearchInput}
              />
              <div className="global-search-results">
                <div className="search-results-header">
                  <span>Search Results ({globalSearchResults.length})</span>
                </div>
                <div className="search-results-list">
                  {globalSearchResults.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}-${index}`}
                      className="search-result-item"
                      onClick={() => {
                        onSearchResultClick(result);
                        handleClearSearchInput();
                      }}
                    >
                      <div className="search-result-avatar">
                        {result.avatar}
                      </div>
                      <div className="search-result-info">
                        <div className="search-result-name">
                          {result.name}
                          {result.type === 'subChat' && (
                            <span className="search-result-parent"> in {result.parentName}</span>
                          )}
                        </div>
                        <div className="search-result-preview">
                          {result.lastMatch.content.substring(0, 60)}
                          {result.lastMatch.content.length > 60 ? '...' : ''}
                        </div>
                      </div>
                      <div className="search-result-badge">
                        {result.type === 'group' && <Users size={12} />}
                        <span>{result.matchCount} {result.matchCount === 1 ? 'match' : 'matches'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {globalSearchTerm && globalSearchResults.length === 0 && (
            <div className="global-search-results">
              <div className="search-results-empty">
                <Search size={24} className="empty-search-icon" />
                <p>No results found for "{globalSearchTerm}"</p>
              </div>
            </div>
          )}
        </div>

        <button 
          className="add-agent-button-topbar"
          onClick={onAddAgent}
          title="Add new agent"
        >
          <Plus size={18} />
          <span>New Agent</span>
        </button>

        <div className="user-menu-container" ref={userMenuRef}>
          <button
            className="user-menu-button"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <User size={18} />
            <span>{username || 'User'}</span>
          </button>

          {showUserMenu && (
            <div className="user-menu animate-slide-in-up">
              <div className="user-menu-header">
                <div className="user-menu-avatar">
                  <User size={20} />
                </div>
                <div>
                  <div className="user-menu-name">{username || 'User'}</div>
                  <div className="user-menu-status text-muted">Active</div>
                </div>
              </div>
              <div className="user-menu-divider"></div>
              <button className="user-menu-item" onClick={onLogout}>
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;
